import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { usePortalUser } from "@/hooks/usePortalUser";
import { buildAppUrl } from "@/lib/url";

function hasTasksGrant(grants: { capability: string }[] | undefined): boolean {
  if (!grants?.length) return false;
  return grants.some((g) => g.capability === "tasks" || g.capability === "site_admin" || g.capability === "manage_users");
}

export function TasksLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { session, loading } = useSession();
  const { portalSession, checking } = usePortalUser(session);

  const next = useMemo(() => {
    const raw = params.get("next");
    if (raw && raw.startsWith("/")) return raw;
    return "/tasks";
  }, [params]);

  useEffect(() => {
    if (loading) return;
    if (!session) return;
    if (checking) return;
    if (hasTasksGrant(portalSession?.grants)) {
      navigate(next, { replace: true });
    }
  }, [checking, loading, navigate, next, portalSession?.grants, session]);

  async function signInGoogle() {
    const redirectTo = buildAppUrl(next);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) console.error(error);
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-blue px-4 text-center text-brand-gray">
      {loading || (session && checking) ? (
        <p className="font-display text-2xl font-bold">Loading…</p>
      ) : session && !hasTasksGrant(portalSession?.grants) ? (
        <>
          <p className="font-display text-2xl font-bold">Access not authorised</p>
          <p className="mt-4 max-w-md text-sm text-brand-gray/90">
            {session.user.email} is not registered for Tasks access. Ask a portal manager to add you in{" "}
            <span className="font-semibold">Admin → Users</span> by selecting{" "}
            <span className="font-semibold">Tasks access</span>.
          </p>
          <button
            type="button"
            onClick={() => void supabase.auth.signOut()}
            className="mt-8 rounded-full bg-brand-maroon px-8 py-3 text-sm font-bold uppercase tracking-wide text-white"
          >
            Sign out
          </button>
          <Link to="/" className="mt-6 text-sm underline">
            Back to site
          </Link>
        </>
      ) : (
        <>
          <p className="font-display text-2xl font-bold">Tasks sign in</p>
          <p className="mt-3 max-w-sm text-sm text-brand-gray/90">
            Sign in with Google to access the private OBA Tasks list.
          </p>
          <button
            type="button"
            onClick={() => void signInGoogle()}
            className="mt-8 rounded-full bg-white px-8 py-3 text-sm font-bold uppercase tracking-wide text-brand-blue shadow-md"
          >
            Continue with Google
          </button>
          <Link to="/" className="mt-10 text-sm font-semibold underline">
            Back to site
          </Link>
        </>
      )}
    </div>
  );
}

